import React, { useEffect, useMemo, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import InputField from "../../../components/InputField";
import SearchIcon from "../../../assets/SearchIcon";
import { Link } from "react-router-dom";
import Button from "../../../components/Button";
import ManagementTableLayout from "../../../components/MangementTable";
import Pagination from "../../../components/Pagination";
import { useRecoilValue } from "recoil";
import { stateAtom } from "../../../atom";
import TrashIcon from "../../../assets/TrashIcon";
import CreateQuotation from "../CreateQuotation";
import EditIcon from "../../../assets/EditIcon";
import Modal from "../../../components/common/Modal";
import { protectedAxiosInstance } from "../../../api/axiosManagement";
import { toast } from "react-toastify";
import { checkPermissions } from "../../../utils/helper";
import UpArrow2 from "../../../assets/svg-tsx/UpArrow2";
import FilterIcon from "../../../assets/svg-tsx/FilterIcon";
import SelectField2 from "../../../components/SelectedField2";
import { Tooltip } from "antd";

function convertDateFormat(inputDate: string) {
  if (!inputDate.endsWith("Z")) {
    inputDate = inputDate + "Z";
  }

  const date = new Date(inputDate);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format");
  }

  // Extract UTC parts
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();

  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
}


const typeList = [
  {
    value: "PROJECT",
    label: "Project",
  },
  {
    value: "REGULAR",
    label: "Regular",
  },
];

const Table = ({
  page,
  setPage,
  pageLength,
  setPageLength,
  searchValue,
  setSearchValue,
  quotationList,
  isLoading,
  totalCount,
  setTotalCount,
  getQuotationList,
  setIsLoading,
  sortBy,
  setSortBy,
  filteredType,
  setFilteredType,
}: any) => {
  const stateList = useRecoilValue(stateAtom);
  const [deleteModal, setDeleteModal] = useState<any>("");

  //Permissions
  const hasCreatePermission = checkPermissions("create_quotation");
  const hasDeletePermission = checkPermissions("delete_quotation");
  const hasEditPermission = checkPermissions("edit_quotation");

  const hideActionColumn = !hasDeletePermission && !hasEditPermission;

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await protectedAxiosInstance.delete(
        `/admin/quotation/${id}`
      );
      getQuotationList();
    } catch (error: any) {
      toast.error("Error while deleting quotation");
      console.log(error);
    } finally {
      setIsLoading(false);
      setDeleteModal("");
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // If already sorting by this field, toggle to descending
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      // If already sorting by this field descending, clear sort
      setSortBy("");
    } else {
      // Otherwise, sort by this field ascending
      setSortBy(field);
    }
  };

  // Helper function to determine sort direction for a field
  const getSortDirection = (field: string) => {
    if (sortBy === field) return "asc";
    if (sortBy === `-${field}`) return "desc";
    return "none";
  };

  const COLUMN = [
    {
      id: "created_at",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center py-2 cursor-pointer gap-1"
          onClick={() => handleSort("created_at")}
        >
          Created On
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("created_at") === "desc"
                  ? "rotate-180"
                  : getSortDirection("created_at") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "created_at",
      Cell: ({ value }: any) => {
        return (
          <div className="px-2 py-2">
            {value ? convertDateFormat(value) : "-"}
          </div>
        );
      },
    },
    {
      id: "name",
      Header: ({ column }: { column: any }) => {
        return (
          <div
            className="flex items-center justify-between gap-1 cursor-pointer"
            onClick={() => handleSort("name")}
          >
            Name
            <div className="w-4 h-4 flex items-center justify-center">
              <UpArrow2
                className={`h-4 w-4 transition-transform ${
                  getSortDirection("name") === "desc"
                    ? "rotate-180"
                    : getSortDirection("name") === "none"
                    ? "opacity-50"
                    : ""
                }`}
              />
            </div>
          </div>
        );
      },
      accessor: "num",
      Cell: ({ row }: { row: any }) => (
        <div className="hover:underline underline-offset-2 hover:scale-105 hover:pl-1">
          <Link to={`/quotations/${row?.original?.id}`}>
            {row?.original?.name || "-"}
          </Link>
        </div>
      ),
    },
    {
      id: "quotation_number",
      Header: ({ column }: { column: any }) => {
        return (
          <div
            className="flex items-center justify-between gap-1 cursor-pointer"
            onClick={() => handleSort("num")}
          >
            Quotation #
            <div className="w-4 h-4 flex items-center justify-center">
              <UpArrow2
                className={`h-4 w-4 transition-transform ${
                  getSortDirection("num") === "desc"
                    ? "rotate-180"
                    : getSortDirection("num") === "none"
                    ? "opacity-50"
                    : ""
                }`}
              />
            </div>
          </div>
        );
      },
      accessor: "num",
      Cell: ({ row }: { row: any }) => (
        <div className="hover:underline underline-offset-2 hover:scale-105 hover:pl-1">
          <Link to={`/quotations/${row?.original?.id}`}>
            #{row?.original?.num || "-"}
          </Link>
        </div>
      ),
    },
    {
      id: "kind",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-start items-center">Type</div>
      ),
      accessor: "kind",
      Cell: ({ value }: any) => (
        <div className="!capitalize">{value || "-"}</div>
      ),
    },
    {
      id: "project_name",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-start items-center">Project Name</div>
      ),
      accessor: "project_name",
      Cell: ({ value }: any) => <div>{value || "-"}</div>,
    },
    {
      id: "items_count",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-start items-center gap-1 cursor-pointer"
          onClick={() => handleSort("no_of_items")}
        >
          Items
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("no_of_items") === "desc"
                  ? "rotate-180"
                  : getSortDirection("no_of_items") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      accessor: "count_items",
      Cell: ({ value }: any) => <div>{value || 0}</div>,
    },
    ...(!hideActionColumn
      ? [
          {
            id: "actions",
            Header: ({ column }: { column: any }) => (
              <div className="flex justify-start items-center">Actions</div>
            ),
            accessor: "id",
            Cell: ({ value, row }: { value: any; row: any }) => (
              <div className="flex gap-x-5 px-2 items-center">
                <Tooltip title="Delete">
                  {hasDeletePermission && (
                    <button
                      className="cursor-pointer"
                      disabled={isLoading}
                      onClick={() => {
                        setDeleteModal(row?.original?.id);
                      }}
                    >
                      <TrashIcon width="16" height="16" />
                    </button>
                  )}
                </Tooltip>

                <Tooltip title="Edit">
                  {hasEditPermission && (
                    <button disabled={isLoading}>
                      <Link
                        to={`/quotations/${row?.original?.id}`}
                        className="cursor-pointer"
                      >
                        <EditIcon />
                      </Link>
                    </button>
                  )}
                </Tooltip>
              </div>
            ),
          },
        ]
      : []),
  ];

  const columns = useMemo(() => COLUMN, [quotationList]);
  const data = useMemo(() => quotationList || [], [quotationList]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
    },
    useSortBy,
    usePagination
  );

  const handlePageChange = (page: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: any) => {
    setPageLength(size);
  };

  return (
    <div className="h-full">
      <div className="flex flex-row justify-between items-center w-full px-3 py-1">
        <div className="flex gap-4 w-[max-content] mt-4">
          <div className=" w-[235px]">
            <InputField
              onChange={(e: string) => {
                setSearchValue(e);
                if (page !== 1) {
                  setPage(1);
                }
              }}
              placeholder="Search Quotation Number"
              value={searchValue}
              rightIcon={<SearchIcon />}
            />
          </div>

          {hasCreatePermission && (
            <CreateQuotation getQuotationList={getQuotationList} />
          )}
        </div>
        <div className="mb-3">
          <SelectField2
            disabled={isLoading}
            label="Type"
            placeholder="Select project type"
            value={filteredType}
            onChange={(e) => {
              if (e === filteredType) {
                setFilteredType("");
              } else {
                setFilteredType(e);
              }
              setPage(1);
            }}
            removeOption={() => {
              setFilteredType("");
            }}
            options={typeList?.map((el: any) => ({
              value: el.value,
              label: el.label,
            }))}
            leftIcon={<FilterIcon />}
          />
        </div>
      </div>

      <div className="h-[calc(100%-5.5rem)] w-full relative -z-0">
        <ManagementTableLayout
          tableInstance={tableInstance}
          loading={isLoading}
        />
      </div>
      {isLoading ? (
        ""
      ) : (
        <div className="w-full">
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {deleteModal && (
        <Modal
          top
          header="Delete Product"
          footer={
            <div className="flex gap-x-3">
              <Button
                variant="primary"
                label="Delete"
                onClick={() => handleDelete(deleteModal)}
                disabled={isLoading}
              />
              <Button
                variant="secondary"
                label="Cancel"
                onClick={() => {
                  setDeleteModal("");
                }}
                disabled={isLoading}
              />
            </div>
          }
          handleCancel={() => setDeleteModal("")}
        >
          <p className="text-left">
            Are you sure you want to delete the Quotation?
          </p>
        </Modal>
      )}
    </div>
  );
};

export default Table;
